import React, { useMemo } from "react";
import { useLocation, Link } from "react-router-dom";

type StatusKind = "success" | "error";

function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

function mapReason(reason?: string | null) {
    switch (reason) {
        case "missing_token":
            return "Brak tokenu w linku weryfikacyjnym. Otwórz link z maila ponownie lub poproś o wysłanie nowego.";
        case "invalid":
            return "Link weryfikacyjny jest nieprawidłowy. Możliwe powiedzenie: link został uszkodzony lub już wygasł.";
        case "used":
            return "Ten link został już użyty. Twoje konto może być już zweryfikowane — spróbuj się zalogować.";
        case "expired":
            return "Link weryfikacyjny wygasł. Poproś o ponowne wysłanie wiadomości weryfikacyjnej.";
        case "server":
            return "Wystąpił błąd serwera podczas weryfikacji. Spróbuj ponownie później.";
        default:
            return "Weryfikacja nie powiodła się. Spróbuj ponownie lub poproś o nowy link weryfikacyjny.";
    }
}

export default function EmailVerified() {
    const q = useQuery();

    const okParam = (q.get("ok") || "").trim();
    const reason = (q.get("reason") || "").trim() || null;

    const ok =
        okParam === "1" || okParam.toLowerCase() === "true" || okParam === "ok";

    const status: StatusKind = ok ? "success" : "error";

    const title = ok ? "E-mail zweryfikowany ✅" : "Weryfikacja nieudana ❌";

    const message = ok
        ? "Dziękujemy! Twoje konto zostało zweryfikowane. Możesz teraz zalogować się w aplikacji."
        : mapReason(reason);

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div
                    style={{
                        ...styles.badge,
                        background: status === "success" ? "#14532d" : "#7f1d1d",
                    }}
                >
                    {status === "success" ? "SUKCES" : "BŁĄD"}
                </div>

                <h1 style={styles.h1}>{title}</h1>
                <p style={styles.p}>{message}</p>

                <div style={styles.actions}>
                    <Link to="/" style={{ ...styles.btn, ...styles.btnGhost }}>
                        Strona główna
                    </Link>
                </div>

                {!ok && reason && (
                    <p style={styles.hint}>
                        Kod błędu: <code style={styles.code}>{reason}</code>
                    </p>
                )}
            </div>
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    page: {
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
    },
    card: {
        width: "100%",
        maxWidth: 560,
        background: "#fff",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 16,
        padding: 22,
        boxShadow: "0 18px 40px rgba(0,0,0,0.08)",
    },
    badge: {
        display: "inline-block",
        color: "#fff",
        fontWeight: 800,
        fontSize: 12,
        letterSpacing: 0.6,
        padding: "6px 10px",
        borderRadius: 999,
        marginBottom: 12,
    },
    h1: {
        margin: "0 0 10px",
        fontSize: 22,
        lineHeight: 1.2,
    },
    p: {
        margin: "0 0 16px",
        fontSize: 14.5,
        lineHeight: 1.6,
        opacity: 0.9,
    },
    actions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        marginTop: 6,
    },
    btn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: 40,
        padding: "0 14px",
        borderRadius: 10,
        textDecoration: "none",
        fontWeight: 800,
        fontSize: 13.5,
    },
    btnPrimary: {
        background: "#111827",
        color: "#fff",
    },
    btnGhost: {
        background: "#f3f4f6",
        color: "#111827",
    },
    hint: {
        marginTop: 14,
        fontSize: 12.5,
        opacity: 0.75,
    },
    code: {
        background: "#f3f4f6",
        padding: "2px 6px",
        borderRadius: 8,
    },
};
